import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../../services/site/InfoService';
import VideoPlayer from '../../../Components/VideoPlayer';
import onboardingVideo from "../../../assets/videos/template.mp4";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: any;
}> = ({ children, className = '', variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_co-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_co-hero-title">
                Client Onboarding Platform
              </h1>
              <h2 className="subtitle is-4 sds_co-hero-subtitle mb-5">
                Accelerate Client Acquisition with Intelligent Automation
              </h2>
              <div className="content is-size-5 sds_co-hero-description">
                <p className="mb-4">
                  <strong>Win Clients Faster:</strong> Our smart client onboarding solution combines
                  intelligent forms, AI-powered document processing, and seamless digital signatures
                  to transform how you acquire and onboard new clients.
                </p>
                <p className="mb-4">
                  Capture client information, collect required documents, and let AI automatically
                  extract data and populate templates. Get contracts signed digitally in minutes!
                </p>
              </div>
              <div className="sds_co-feature-badges">
                <span className="tag is-large sds_co-badge">🤝 Fast Onboarding</span>
                <span className="tag is-large sds_co-badge">🤖 AI-Powered</span>
                <span className="tag is-large sds_co-badge">✍️ E-Signatures</span>
                <span className="tag is-large sds_co-badge">🔒 Bank-Secure</span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_co-hero-visual"
            >
              <div className="sds_co-animation">
                <motion.div
                  className="sds_co-icon-large"
                  animate={{
                    rotate: [0, -5, 5, -5, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  🤝
                </motion.div>
                <div className="sds_co-floating-elements">
                  {['📋', '✅', '💼', '📊'].map((icon, i) => (
                    <motion.div
                      key={i}
                      className="sds_co-floating-element"
                      animate={{ y: [-10, 10, -10] }}
                      transition={{ duration: 2 + i, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {icon}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Featured Video - Client Onboarding Demo */}
        <VideoPlayer
          videoSrc={onboardingVideo}
          title="Client Onboarding Made Simple"
          description="Watch how to onboard clients efficiently with smooth data extraction, document collection, and legally compliant digital signatures - all in under 5 minutes"
          lightBackground={false}
        />
      </div>
    </AnimatedSection>
  );
};

// How It Works Section
const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      number: 1,
      icon: '📝',
      title: 'Client Information Form',
      desc: 'Collect basic client details through smart forms - company info, contact details, business type, and service requirements.',
      features: ['Smart web forms', 'Progressive disclosure', 'Auto-validation', 'Multi-step wizard']
    },
    {
      number: 2,
      icon: '📄',
      title: 'Document Checklist',
      desc: 'Present a customized checklist of required documents based on client type and services selected.',
      features: ['Dynamic checklist', 'Service-specific docs', 'Upload tracking', 'Format validation']
    },
    {
      number: 3,
      icon: '☁️',
      title: 'Document Upload',
      desc: 'Clients upload required documents (business license, ID proof, financial docs) through secure portal.',
      features: ['Secure upload', 'Multiple formats', 'Drag & drop', 'Cloud storage']
    },
    {
      number: 4,
      icon: '🤖',
      title: 'AI Data Extraction',
      desc: 'Our AI automatically extracts key information from uploaded documents - company details, registration numbers, addresses.',
      features: ['Smart OCR', 'Data validation', '99% accuracy', 'Instant processing']
    },
    {
      number: 5,
      icon: '📋',
      title: 'Template Auto-Fill',
      desc: 'AI populates service agreements, NDAs, and contracts with extracted data, ready for review and signing.',
      features: ['Template library', 'Custom templates', 'Smart mapping', 'Preview mode']
    },
    {
      number: 6,
      icon: '✍️',
      title: 'Digital Signatures',
      desc: 'Both parties sign agreements digitally - legally binding, instant, and completely paperless.',
      features: ['E-signature flow', 'Multi-party signing', 'Email notifications', 'Audit trail']
    },
    {
      number: 7,
      icon: '💾',
      title: 'Export & Archive',
      desc: 'Download complete client data package with all signed documents, automatically stored in your CRM.',
      features: ['CRM integration', 'Automated export', 'Secure archive', 'Easy retrieval']
    }
  ];

  return (
    <AnimatedSection className="sds_co-process section sds_co-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_co-section-title">How It Works - 7 Simple Steps</h2>
          <p className="subtitle is-5">From prospect to client in minutes</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, idx) => (
            <motion.div key={idx} className="column is-4" variants={fadeInUp}>
              <motion.div
                className="sds_co-process-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_co-step-number">{step.number}</div>
                <div className="sds_co-step-icon">{step.icon}</div>
                <h4 className="title is-5">{step.title}</h4>
                <p className="is-size-7 has-text-grey mb-4">{step.desc}</p>
                <ul className="sds_co-feature-list">
                  {step.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <span className="icon has-text-success is-small">
                        <i className="fas fa-check"></i>
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Industry Solutions Section
const IndustrySolutionsSection: React.FC = () => {
  const industries = [
    {
      icon: '🏦',
      title: 'Financial Services',
      color: '#1976D2',
      desc: 'Banks, insurance, investment firms',
      requirements: ['KYC documents', 'Financial statements', 'Compliance forms', 'Risk assessments']
    },
    {
      icon: '⚖️',
      title: 'Legal & Law Firms',
      color: '#7B1FA2',
      desc: 'Law practices, legal consultancies',
      requirements: ['Client agreements', 'Retainer contracts', 'Conflict checks', 'Engagement letters']
    },
    {
      icon: '💼',
      title: 'Consulting Services',
      color: '#388E3C',
      desc: 'Business, IT, management consulting',
      requirements: ['Service agreements', 'SOW documents', 'NDAs', 'Project proposals']
    },
    {
      icon: '🏗️',
      title: 'Real Estate',
      color: '#F57C00',
      desc: 'Property management, brokers',
      requirements: ['Property docs', 'Client verification', 'Lease agreements', 'Commission contracts']
    },
    {
      icon: '🎓',
      title: 'Education & Training',
      color: '#0097A7',
      desc: 'Schools, training institutes',
      requirements: ['Enrollment forms', 'Student documents', 'Fee agreements', 'Course contracts']
    },
    {
      icon: '💻',
      title: 'IT & Software',
      color: '#C62828',
      desc: 'Software companies, tech services',
      requirements: ['Service agreements', 'MSAs', 'NDAs', 'SLA documents']
    }
  ];

  return (
    <AnimatedSection className="sds_co-industries section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_co-section-title">Industry-Specific Solutions</h2>
          <p className="subtitle is-5">Tailored for your business type</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {industries.map((industry, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_co-industry-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderTopColor: industry.color }}
              >
                <div className="sds_co-industry-icon" style={{ background: `linear-gradient(135deg, ${industry.color}, ${industry.color}dd)` }}>
                  {industry.icon}
                </div>
                <h3 className="title is-5 has-text-centered mb-2">{industry.title}</h3>
                <p className="has-text-centered has-text-grey-light mb-4 is-size-7">{industry.desc}</p>
                <div className="sds_co-requirements">
                  <p className="has-text-weight-bold mb-2 is-size-7">Required Documents:</p>
                  <ul className="sds_co-req-list">
                    {industry.requirements.map((req, i) => (
                      <li key={i}>
                        <span className="icon has-text-success is-small">
                          <i className="fas fa-file-alt"></i>
                        </span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Key Features Section
const KeyFeaturesSection: React.FC = () => {
  const features = [
    {
      icon: '📝',
      title: 'Smart Forms',
      desc: 'Intelligent forms that adapt based on client type and service selection.',
      color: '#4CAF50'
    },
    {
      icon: '🤖',
      title: 'AI Extraction',
      desc: 'Automatic data extraction from business documents and certificates.',
      color: '#2196F3'
    },
    {
      icon: '📋',
      title: 'Template Engine',
      desc: 'Extensive library of customizable agreement and contract templates.',
      color: '#FF9800'
    },
    {
      icon: '✍️',
      title: 'E-Signatures',
      desc: 'Integrated digital signature workflows for instant contract execution.',
      color: '#9C27B0'
    },
    {
      icon: '🔗',
      title: 'CRM Integration',
      desc: 'Seamless integration with Salesforce, HubSpot, and other CRM systems.',
      color: '#F44336'
    },
    {
      icon: '📊',
      title: 'Analytics Dashboard',
      desc: 'Track onboarding metrics, conversion rates, and bottlenecks.',
      color: '#00BCD4'
    }
  ];

  return (
    <AnimatedSection className="sds_co-features section sds_co-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_co-section-title has-text-white">Powerful Features for Sales Teams</h2>
          <p className="subtitle is-5 has-text-white-ter">Everything you need to close deals faster</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_co-feature-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_co-feature-icon" style={{ background: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="title is-5 mt-4 has-text-white">{feature.title}</h3>
                <p className="has-text-white-ter">{feature.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Benefits Section
const BenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: '⚡',
      title: '5x Faster Client Acquisition',
      desc: 'Close deals in hours instead of weeks',
      stats: '80% faster'
    },
    {
      icon: '💰',
      title: 'Increase Conversion by 60%',
      desc: 'Reduce drop-offs with streamlined process',
      stats: '+60% conversion'
    },
    {
      icon: '😊',
      title: 'Superior Client Experience',
      desc: 'Modern, professional onboarding impresses clients',
      stats: '98% satisfaction'
    },
    {
      icon: '📉',
      title: 'Reduce Costs by 70%',
      desc: 'Eliminate manual work, paper, and errors',
      stats: '₹80,000+ savings'
    },
    {
      icon: '🔒',
      title: 'Complete Compliance',
      desc: 'Automated compliance checks and audit trails',
      stats: '100% compliant'
    },
    {
      icon: '📱',
      title: 'Mobile-Ready',
      desc: 'Clients can complete onboarding on any device',
      stats: 'Any device, anywhere'
    }
  ];

  return (
    <AnimatedSection className="sds_co-benefits section sds_co-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_co-section-title">Why Businesses Love Our Platform</h2>
          <p className="subtitle is-5">Real results that drive growth</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {benefits.map((benefit, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_co-benefit-card"
                whileHover={{ y: -10, boxShadow: '0 15px 40px rgba(0,0,0,0.12)' }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_co-benefit-icon">{benefit.icon}</div>
                <h3 className="title is-5">{benefit.title}</h3>
                <p className="has-text-grey mb-3">{benefit.desc}</p>
                <div className="sds_co-stats-badge">{benefit.stats}</div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Security Section
const SecuritySection: React.FC = () => {
  const securityFeatures = [
    { icon: '🔐', title: 'Bank-Grade Encryption', desc: '256-bit SSL encryption for all data and documents' },
    { icon: '🛡️', title: 'Data Privacy', desc: 'GDPR and privacy law compliant data handling' },
    { icon: '📜', title: 'Complete Audit Trail', desc: 'Track every action and document change' },
    { icon: '✅', title: 'Legal Compliance', desc: 'E-signatures compliant with IT Act 2000 and e-SIGN' },
    { icon: '☁️', title: 'Secure Cloud', desc: 'Enterprise-grade cloud infrastructure with backups' },
    { icon: '🔒', title: 'Access Control', desc: 'Role-based permissions and authentication' }
  ];

  return (
    <AnimatedSection className="sds_co-security section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_co-section-title">🔒 Enterprise Security & Compliance</h2>
          <p className="subtitle is-5">Your client data is always protected</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {securityFeatures.map((feature, index) => (
            <motion.div key={index} className="column is-4" variants={fadeInUp}>
              <div className="sds_co-security-card">
                <div className="sds_co-security-icon">{feature.icon}</div>
                <h4 className="title is-5 has-text-centered">{feature.title}</h4>
                <p className="has-text-centered has-text-grey">{feature.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_co-cta section">
      <div className="container">
        <div className="box sds_co-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">Ready to Accelerate Client Onboarding?</h2>
              <p className="subtitle is-4 mb-6">
                Join hundreds of businesses who are winning more clients, faster
              </p>

              <div className="sds_co-offer-badge mb-5">
                {/* <p className="is-size-3 has-text-weight-bold mb-2">🎁 Limited Time Offer</p> */}
                <p className="is-size-4">First 2 Client Onboardings FREE!</p>
                <p className="is-size-6 mt-3">No credit card • Quick setup • Full features • Cancel anytime</p>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a
                  href={`tel:${InfoService.mobile_no_1}`}
                  className="button is-primary is-large sds_co-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a
                  href={InfoService.loginLink}
                  className="button is-info is-large sds_co-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Get Free Trial</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const ClientOnboarding: React.FC = () => {
  return (
    <div className="sds_co-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions - Client Onboarding Platform with AI & Digital Signatures</h1>
        <meta name="description" content="Accelerate client acquisition with AI-powered onboarding. Smart forms, document automation, and digital signatures. Close deals 5x faster. Secure and compliant." />
        <meta name="keywords" content="client onboarding, customer onboarding, digital signatures, AI document processing, sales automation, CRM integration, HDT Solutions" />
      </div>

      <HeroSection />
      <HowItWorksSection />
      <IndustrySolutionsSection />
      <KeyFeaturesSection />
      <BenefitsSection />
      <SecuritySection />
      <CTASection />

      <style>{`
        /* Base Styles */
        .sds_co-wrapper {
          background: #ffffff;
          min-height: 100vh;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        /* Brand Colors */
        :root {
          --primary-teal: #00ACC1;
          --primary-blue: #1976D2;
          --dark-blue: #0D47A1;
          --success-green: #4CAF50;
        }

        /* Hero Section */
        .sds_co-hero {
          background: linear-gradient(135deg, #E0F7FA 0%, #E3F2FD 100%);
          padding: 4rem 1.5rem;
        }

        .sds_co-hero-title {
          color: var(--dark-blue);
          font-weight: 800;
          line-height: 1.2;
        }

        .sds_co-hero-subtitle {
          color: var(--primary-teal);
          font-weight: 600;
        }

        .sds_co-hero-description {
          color: #555;
          line-height: 1.8;
        }

        .sds_co-feature-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 2rem;
        }

        .sds_co-badge {
          background: linear-gradient(135deg, var(--primary-teal), var(--primary-blue)) !important;
          color: white !important;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border: none;
          box-shadow: 0 4px 15px rgba(0, 172, 193, 0.3);
        }

        .sds_co-hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .sds_co-animation {
          position: relative;
          width: 100%;
          height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .sds_co-icon-large {
          font-size: 150px;
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
        }

        .sds_co-floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
        }

        .sds_co-floating-element {
          position: absolute;
          font-size: 50px;
          filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1));
        }

        .sds_co-floating-element:nth-child(1) { top: 10%; left: 15%; }
        .sds_co-floating-element:nth-child(2) { top: 60%; left: 10%; }
        .sds_co-floating-element:nth-child(3) { top: 20%; right: 15%; }
        .sds_co-floating-element:nth-child(4) { bottom: 20%; right: 20%; }

        /* Section Title */
        .sds_co-section-title {
          color: var(--dark-blue);
          font-weight: 700;
          position: relative;
          display: inline-block;
        }

        .sds_co-section-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-teal), var(--primary-blue));
          margin: 1rem auto 0;
          border-radius: 2px;
        }

        /* Background Gradients */
        .sds_co-bg-gradient {
          background: linear-gradient(135deg, var(--primary-teal) 0%, var(--primary-blue) 100%);
          color: white;
          padding: 4rem 1.5rem;
        }

        .sds_co-bg-light {
          background: linear-gradient(135deg, #E0F7FA 0%, #E3F2FD 100%);
          padding: 4rem 1.5rem;
        }

        /* Process Section */
        .sds_co-process {
          padding: 4rem 1.5rem;
        }

        .sds_co-process-card {
          background: white;
          padding: 2rem 1.5rem;
          border-radius: 20px;
          text-align: center;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border-top: 4px solid var(--primary-teal);
        }

        .sds_co-process-card:hover {
          box-shadow: 0 12px 35px rgba(0, 172, 193, 0.2);
        }

        .sds_co-step-number {
          background: linear-gradient(135deg, var(--primary-teal), #0097A7);
          color: white;
          width: 45px;
          height: 45px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin: 0 auto 1rem;
          font-size: 1.5rem;
          box-shadow: 0 6px 20px rgba(0, 172, 193, 0.4);
        }

        .sds_co-step-icon {
          font-size: 50px;
          margin-bottom: 1rem;
        }

        .sds_co-feature-list {
          list-style: none;
          padding: 0;
          text-align: left;
        }

        .sds_co-feature-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.4rem;
          color: #555;
          font-size: 0.85rem;
        }

        .sds_co-feature-list li .icon {
          margin-right: 0.5rem;
        }

        /* Industry Section */
        .sds_co-industries {
          padding: 4rem 1.5rem;
        }

        .sds_co-industry-card {
          background: white;
          border-radius: 20px;
          padding: 2rem 1.5rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border-top: 5px solid;
        }

        .sds_co-industry-icon {
          width: 70px;
          height: 70px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 35px;
          margin: 0 auto 1rem;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
        }

        .sds_co-requirements {
          margin-top: 1rem;
        }

        .sds_co-req-list {
          list-style: none;
          padding: 0;
        }

        .sds_co-req-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.4rem;
          color: #555;
          font-size: 0.85rem;
        }

        .sds_co-req-list li .icon {
          margin-right: 0.5rem;
        }

        /* Features Section */
        .sds_co-features {
          padding: 4rem 1.5rem;
        }

        .sds_co-feature-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.2);
          text-align: center;
          transition: all 0.3s ease;
        }

        .sds_co-feature-card:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.4);
        }

        .sds_co-feature-icon {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 0 auto;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        /* Benefits Section */
        .sds_co-benefits {
          padding: 4rem 1.5rem;
        }

        .sds_co-benefit-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: all 0.3s ease;
        }

        .sds_co-benefit-icon {
          font-size: 60px;
          margin-bottom: 1rem;
        }

        .sds_co-stats-badge {
          background: linear-gradient(135deg, var(--primary-teal), var(--primary-blue));
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: bold;
          display: inline-block;
          margin-top: 1rem;
        }

        /* Security Section */
        .sds_co-security {
          padding: 4rem 1.5rem;
        }

        .sds_co-security-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .sds_co-security-icon {
          font-size: 50px;
          margin-bottom: 1rem;
        }

        /* CTA Section */
        .sds_co-cta {
          padding: 4rem 1.5rem;
          background: linear-gradient(135deg, #E0F7FA 0%, #E3F2FD 100%);
        }

        .sds_co-cta-box {
          background: white;
          border-radius: 30px;
          padding: 4rem 2rem;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .sds_co-offer-badge {
          background: linear-gradient(135deg, #FFF9C4, #FFECB3);
          border: 3px solid #FFA726;
          border-radius: 20px;
          padding: 2rem;
          display: inline-block;
        }

        .sds_co-cta-button {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 1.5rem 2.5rem !important;
          border-radius: 50px !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }

        .sds_co-cta-button:hover {
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
        }

        /* Video Placeholder */
        .sds_co-video-container {
          margin: 3rem 0;
          display: flex;
          justify-content: center;
        }

        .sds_co-video-placeholder {
          background: linear-gradient(135deg, rgba(0, 172, 193, 0.1), rgba(25, 118, 210, 0.1));
          border: 3px dashed var(--primary-teal);
          border-radius: 20px;
          padding: 3rem 2rem;
          text-align: center;
          width: 100%;
          max-width: 800px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .sds_co-video-placeholder:hover {
          background: linear-gradient(135deg, rgba(0, 172, 193, 0.15), rgba(25, 118, 210, 0.15));
          border-color: var(--primary-blue);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .sds_co-video-light {
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .sds_co-video-light:hover {
          background: white;
          border-color: white;
        }

        .sds_co-video-placeholder .icon {
          color: var(--primary-teal);
          transition: all 0.3s ease;
        }

        .sds_co-video-placeholder:hover .icon {
          color: var(--primary-blue);
          transform: scale(1.1);
        }

        /* Responsive Design */
        @media screen and (max-width: 768px) {
          .sds_co-hero {
            padding: 2rem 1rem;
          }

          .sds_co-hero-title {
            font-size: 2rem;
          }

          .sds_co-icon-large {
            font-size: 100px;
          }

          .sds_co-floating-element {
            font-size: 35px;
          }

          .sds_co-section-title {
            font-size: 1.75rem;
          }

          .sds_co-video-placeholder {
            padding: 2rem 1rem;
          }

          .sds_co-cta-box {
            padding: 2rem 1rem;
          }

          .sds_co-offer-badge {
            padding: 1.5rem;
          }

          .sds_co-cta-button {
            padding: 1rem 1.5rem !important;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientOnboarding;
